import { isImageNode, isVideoNode } from '@creative/nodes/helpers';
import { IElementProperty } from '@domain/creativeset';
import { AssetReference, OneOfElementAssets } from '@domain/creativeset/element-asset';
import { FeededReference } from '@domain/feed';
import { ImageSizeMode } from '@domain/image';
import { IImageElementDataNode, IMediaElementDataNode, OneOfElementDataNodes } from '@domain/nodes';

export function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
    return new Promise<{ width: number; height: number }>(resolve => {
        const img = new Image();

        img.onload = (): void => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = src;
    });
}

export function getVideoMetadata(src: string): Promise<{ video: HTMLVideoElement; thumbnail: string }> {
    return new Promise(async resolve => {
        const videoUrl = src;
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');

        document.body.appendChild(canvas);

        const videoBlob = await fetch(videoUrl).then(r => r.blob());
        const videoObjectUrl = URL.createObjectURL(videoBlob);

        video.addEventListener('loadeddata', function () {
            video.currentTime = 1;
        });

        video.addEventListener('seeked', () => {
            const context = canvas.getContext('2d')!;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const dataUrl = canvas.toDataURL();
            resolve({
                thumbnail: dataUrl,
                video
            });
        });

        video.src = videoObjectUrl;
    });
}

export const getAssetOfMediaElement = (
    element: IMediaElementDataNode
): OneOfElementAssets | undefined => {
    if (isVideoNode(element)) {
        return element.videoAsset;
    } else if (isImageNode(element)) {
        return element.imageAsset;
    }
};

export const isMediaNode = (element: OneOfElementDataNodes): element is IMediaElementDataNode => {
    return isImageNode(element) || isVideoNode(element);
};

export const isElementMediaAssetLoading = (element: IMediaElementDataNode): boolean => {
    return !!getAssetOfMediaElement(element)?.__loading;
};

export const isImageReference = (property: IElementProperty): boolean => {
    return property.name === AssetReference.Image;
};

export const isFeededImageReference = (property: IElementProperty): boolean => {
    return property.name === FeededReference.Image;
};

export const isVideoReference = (property: IElementProperty): boolean => {
    return property.name === AssetReference.Video;
};

export const isMediaReference = (property: IElementProperty): boolean => {
    return isImageReference(property) || isVideoReference(property);
};

export const determineIsHeavyVideo = (fileSize: number, sizeInMb: number): boolean => {
    const MAX_LIMIT = 50;
    return fileSize > 1024 * 1000 * sizeInMb && !isTooHeavyVideo(fileSize, MAX_LIMIT);
};

export const isTooHeavyVideo = (fileSize: number, maxLimit: number): boolean => {
    return fileSize ? fileSize > 1024 * 1000 * maxLimit : false;
};

export const isImageFile = (file: File): boolean => {
    return file.type.includes('image');
};

export const isVideoFile = (file: File): boolean => {
    return file.type.includes('video');
};

export const isMP4File = (file: File): boolean => {
    return file.type === 'video/mp4';
};

export const isSVGFile = (file: Blob): boolean => {
    return file.type === 'image/svg+xml';
};

export const filterSVGFiles = (files: File[]): File[] => {
    return files.filter(isSVGFile);
};

export const isFilledImage = (element: IImageElementDataNode): boolean => {
    return element.imageSettings.sizeMode === ImageSizeMode.Fill;
};
